import React, { useMemo, useState } from "react";
import YearCalendarBoard from "./YearCalendarBoard";

export default function TeamsBaseEditor({
  machines,
  operatorsSummary,
  noWorkEntries,
  boostEntries,
  onAddNoWork,
  onRemoveNoWork,
  onToggleNoWorkDate,
  onAddBoost,
  onRemoveBoost,
  onToggleDoubleDate,
}) {
  const [calendarYear, setCalendarYear] = useState(2026);
  const [openMachineId, setOpenMachineId] = useState(null);
  const [calendarMode, setCalendarMode] = useState("off");

  const dateMeta = useMemo(() => {
    const out = {};
    if (!openMachineId) return out;

    for (let month = 0; month < 12; month += 1) {
      const daysInMonth = new Date(calendarYear, month + 1, 0).getDate();
      for (let day = 1; day <= daysInMonth; day += 1) {
        const isoDate = `${calendarYear}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        out[isoDate] = {
          tone: "baseline",
          label: "Trabalha (padrao 1x/dia)",
          badge: "1x",
        };
      }
    }

    (noWorkEntries || []).forEach((entry) => {
      if (entry.machineId !== openMachineId) return;
      if (entry.startDate !== entry.endDate) return;
      if (!String(entry.startDate || "").startsWith(`${calendarYear}-`)) return;
      out[entry.startDate] = {
        tone: "warning",
        label: "Não trabalha",
        badge: "0x",
      };
    });

    (boostEntries || []).forEach((entry) => {
      if (entry.machineId !== openMachineId) return;
      if (entry.startDate !== entry.endDate) return;
      if (!String(entry.startDate || "").startsWith(`${calendarYear}-`)) return;
      out[entry.startDate] = {
        tone: "ok",
        label: "2 vagas",
        badge: "2x",
      };
    });

    return out;
  }, [noWorkEntries, boostEntries, openMachineId, calendarYear]);

  const visibleNoWork = useMemo(
    () => (noWorkEntries || []).filter((entry) => entry.machineId === openMachineId),
    [noWorkEntries, openMachineId]
  );
  const visibleBoost = useMemo(
    () => (boostEntries || []).filter((entry) => entry.machineId === openMachineId),
    [boostEntries, openMachineId]
  );

  const handleDayClick = (date) => {
    if (!openMachineId) return;
    if (calendarMode === "off") onToggleNoWorkDate?.(openMachineId, date);
    if (calendarMode === "double") onToggleDoubleDate?.(openMachineId, date);
  };

  return (
    <section className="swb-panel">
      <div className="swb-panel__head">
        <h3>Calendário operacional das máquinas</h3>
        <span className="swb-subtle">Somente calendários (sem cadastro manual de equipes)</span>
      </div>

      <div className="swb-inline-kpis">
        <div className="swb-inline-kpi">
          <span>Total de pessoas</span>
          <strong>{operatorsSummary?.totalRegistered || 0}</strong>
        </div>
        <div className="swb-inline-kpi">
          <span>Operadores recomendados</span>
          <strong>{operatorsSummary?.recommendedOperators || 0}</strong>
        </div>
        <div className="swb-inline-kpi">
          <span>Padrão sem calendário</span>
          <strong>Trabalha sempre · 1x/dia</strong>
        </div>
      </div>

      <section className="swb-subpanel" style={{ marginTop: 12 }}>
        <div className="swb-panel__head">
          <h4>Calendário operacional por prefixo da máquina</h4>
          <div className="swb-mode-toggle" role="group" aria-label="Ação do calendário">
            <button
              type="button"
              className={`swb-btn ${calendarMode === "off" ? "swb-btn--danger" : ""}`}
              onClick={() => setCalendarMode("off")}
            >
              Não trabalha
            </button>
            <button
              type="button"
              className={`swb-btn ${calendarMode === "double" ? "swb-btn--primary" : ""}`}
              onClick={() => setCalendarMode("double")}
            >
              2 vagas
            </button>
          </div>
        </div>

        <p className="swb-subtle">
          Padrão (sem cadastro): trabalha sempre e 1x por dia, ano todo.
        </p>

        <div className="swb-accordion-list">
          {(machines || []).map((machine) => {
            const isOpen = openMachineId === machine.id;
            const offCount = (noWorkEntries || []).filter((e) => e.machineId === machine.id).length;
            const doubleCount = (boostEntries || []).filter((e) => e.machineId === machine.id).length;

            return (
              <section key={machine.id} className={`swb-accordion ${isOpen ? "is-open" : ""}`}>
                <button
                  type="button"
                  className="swb-accordion__head"
                  onClick={() => setOpenMachineId((prev) => (prev === machine.id ? null : machine.id))}
                >
                  <span>{machine.prefix} ({machine.code})</span>
                  <small>Não trabalha: {offCount} · 2 vagas: {doubleCount}</small>
                </button>

                {isOpen ? (
                  <div className="swb-accordion__body">
                    <div className="swb-inline-actions" style={{ marginBottom: 8 }}>
                      <button type="button" className="swb-btn" onClick={() => onAddNoWork(machine.id)}>
                        Adicionar intervalo (não trabalha)
                      </button>
                      <button type="button" className="swb-btn" onClick={() => onAddBoost(machine.id)}>
                        Adicionar intervalo (2 vagas)
                      </button>
                    </div>

                    <YearCalendarBoard
                      year={calendarYear}
                      onYearChange={setCalendarYear}
                      dateMeta={dateMeta}
                      onDayClick={handleDayClick}
                      subtitle="Clique no dia usando o modo selecionado."
                    />

                    <div className="swb-base-subgrid">
                      <div className="swb-subpanel">
                        <div className="swb-panel__head">
                          <h4>Registros: não trabalha</h4>
                          <span className="swb-subtle">{visibleNoWork.length}</span>
                        </div>
                        <div className="swb-list">
                          {visibleNoWork.slice(0, 12).map((entry) => (
                            <div key={entry.id} className="swb-list-item">
                              <div className="swb-list-item__content">
                                <strong>
                                  {entry.startDate === entry.endDate
                                    ? entry.startDate
                                    : `${entry.startDate} até ${entry.endDate}`}
                                </strong>
                                <span>{entry.reason || "Não trabalha"}</span>
                              </div>
                              <button
                                type="button"
                                className="swb-btn swb-btn--danger"
                                onClick={() => onRemoveNoWork(entry.id)}
                              >
                                Excluir
                              </button>
                            </div>
                          ))}
                          {!visibleNoWork.length ? <div className="swb-empty">Nenhum registro.</div> : null}
                        </div>
                      </div>

                      <div className="swb-subpanel">
                        <div className="swb-panel__head">
                          <h4>Registros: 2 vagas</h4>
                          <span className="swb-subtle">{visibleBoost.length}</span>
                        </div>
                        <div className="swb-list">
                          {visibleBoost.slice(0, 12).map((entry) => (
                            <div key={entry.id} className="swb-list-item">
                              <div className="swb-list-item__content">
                                <strong>
                                  {entry.startDate === entry.endDate
                                    ? entry.startDate
                                    : `${entry.startDate} até ${entry.endDate}`}
                                </strong>
                                <span>2 vagas</span>
                              </div>
                              <button
                                type="button"
                                className="swb-btn swb-btn--danger"
                                onClick={() => onRemoveBoost(entry.id)}
                              >
                                Excluir
                              </button>
                            </div>
                          ))}
                          {!visibleBoost.length ? <div className="swb-empty">Nenhum registro.</div> : null}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      </section>
    </section>
  );
}
